import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onSave: () => void;
  isSaving?: boolean;
}

export function UnsavedChangesModal({
  isOpen,
  onCancel,
  onDiscard,
  onSave,
  isSaving = false,
}: UnsavedChangesModalProps) {
  // Use Portal to ensure the modal is always at the top of the body
  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[9998] backdrop-blur-[2px]"
            onClick={onCancel}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
              className="w-full max-w-sm pointer-events-auto overflow-hidden
                         backdrop-blur-xl bg-background/60 border border-white/20 dark:border-white/10 
                         shadow-2xl rounded-2xl sm:rounded-3xl p-6"
            >
              <div className="flex flex-col items-center text-center">
                <div className="h-14 w-14 rounded-full bg-warning/10 flex items-center justify-center mb-5">
                  <AlertTriangle className="h-7 w-7 text-warning" />
                </div>
                
                <h2 className="text-xl font-bold text-foreground">Unsaved Changes</h2>
                <p className="text-sm text-muted-foreground mt-2 px-2 leading-relaxed">
                  You have modified your settings. Do you want to save your progress or discard the changes?
                </p>
                
                <div className="flex flex-col w-full gap-3 mt-8">
                  <Button 
                    onClick={onSave}
                    disabled={isSaving}
                    className="w-full rounded-xl py-6 font-bold shadow-lg shadow-primary/20"
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </Button>
                  
                  <Button 
                    onClick={onDiscard}
                    variant="outline"
                    className="w-full rounded-xl py-6 font-semibold bg-background/40 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all"
                  >
                    Discard Changes
                  </Button>
                  
                  <Button 
                    onClick={onCancel}
                    variant="ghost"
                    className="w-full rounded-xl py-6 font-medium text-muted-foreground hover:text-foreground"
                  >
                    Stay on Page
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
